import { type OptimizedSubscription } from '@bbkAdminRedux/rtkq/optimizations.slice';
import {
  getFormattedAmount,
  getIso4217CurrencyFractionDigits,
  minorUnitsToMajorUnits,
} from '@bbkPricingPage/lib/currencies';

const MIXED_CURRENCIES = 'Mixed currencies';

const MIN_CATALOG_FRACTION_DIGITS = 0;
const MAX_CATALOG_FRACTION_DIGITS = 10;

/** Plan/addon catalog row: ISO code + optional Chargebee multi-decimal width. */
type CatalogMoneyDigitsRow = {
  currency_code?: string | null;
  decimal_places?: number | null;
};

/**
 * Max {@code decimal_places} from catalog rows in {@code currencyCode} when at least one
 * matching row defines it; otherwise null (use ISO 4217 only).
 */
export function maxDefinedCatalogFractionDigitsForCurrency(
  catalog: ReadonlyArray<CatalogMoneyDigitsRow>,
  currencyCode: string | null | undefined
): number | null {
  const raw = currencyCode?.trim() ?? '';
  const cur = raw.toUpperCase();
  if (!cur || catalog.length === 0) {
    return null;
  }
  let best: number | null = null;
  for (const p of catalog) {
    if (p.currency_code?.trim().toUpperCase() !== cur) {
      continue;
    }
    const d = p.decimal_places;
    if (d == null || !Number.isFinite(d)) {
      continue;
    }
    const n = Math.min(
      MAX_CATALOG_FRACTION_DIGITS,
      Math.max(MIN_CATALOG_FRACTION_DIGITS, Math.trunc(d))
    );
    best = best == null ? n : Math.max(best, n);
  }
  return best;
}

/**
 * Fraction digits for Intl currency formatting in Renewal Optimization: at least ISO 4217 digits
 * for the currency, and at most the larger of ISO and catalog {@code decimal_places} when
 * Chargebee multi-decimal is configured. Pipeline money integers are still converted with
 * {@link minorUnitsToMajorUnits} (ISO minor only); this value only widens min/max fraction digits
 * in the formatter.
 *
 * <p>Zero-decimal ISO currencies (e.g. JPY) always return {@code 0}; fractional catalog digits do
 * not apply per Chargebee rules.
 */
export function optimizerMoneyFractionDigits(
  catalog: ReadonlyArray<CatalogMoneyDigitsRow>,
  currencyCode: string | null | undefined
): number {
  const code = currencyCode?.trim().toUpperCase() || 'USD';
  const iso = getIso4217CurrencyFractionDigits(code);
  if (iso === 0) {
    return 0;
  }
  const catalogDigits = maxDefinedCatalogFractionDigitsForCurrency(
    catalog,
    code
  );
  return catalogDigits == null ? iso : Math.max(iso, catalogDigits);
}

/**
 * Converts API / pipeline amounts from **ISO 4217** minor units (e.g. US cents) to major units.
 * Catalog {@code decimal_places} does not change this scale; use {@link optimizerMoneyFractionDigits}
 * only when passing {@code fractionDigits} into currency formatters.
 */
export function optimizerPriceMinorToMajor(
  minorAmount: number,
  _catalog: ReadonlyArray<CatalogMoneyDigitsRow>,
  currencyCode: string | null | undefined
): number {
  const code = currencyCode?.trim().toUpperCase() || 'USD';
  return minorUnitsToMajorUnits(minorAmount, code);
}

/**
 * Formats an amount in ISO 4217 minor units for display; catalog widens fraction digits only.
 */
export function formatOptimizerMinorMoney(
  minorAmount: number,
  currencyCode: string | null | undefined,
  catalog: ReadonlyArray<CatalogMoneyDigitsRow>
): string {
  const code = (currencyCode?.trim() || 'USD').toUpperCase();
  const fd = optimizerMoneyFractionDigits(catalog, code);
  const major = optimizerPriceMinorToMajor(minorAmount, catalog, code);
  return !Number.isFinite(major)
    ? getFormattedAmount(0, code, fd)
    : getFormattedAmount(major, code, fd);
}

function subscriptionsHaveMultipleCurrencies(
  subscriptions: OptimizedSubscription[]
): boolean {
  const codes = new Set(
    subscriptions.map((s) => (s.currency ?? 'USD').toUpperCase())
  );
  return codes.size > 1;
}

/**
 * Formats an ARR amount given in **ISO 4217 minor** units (API / pipeline). Converts to major
 * units with {@code 10^ISO}; {@code options.moneyFractionDigits} (e.g. catalog multi-decimal) only
 * widens {@code maximumFractionDigits} for display, it must not rescale the integer.
 */
export function formatArrForDisplay(
  amountMinor: number,
  currencyCode?: string | null,
  options: {
    hasMultipleCurrencies?: boolean;
    leadingPlusForPositive?: boolean;
    /** Catalog / UI: minimum and maximum fraction digits for formatting after ISO minor→major. */
    moneyFractionDigits?: number;
  } = {}
): string {
  if (options.hasMultipleCurrencies) {
    return MIXED_CURRENCIES;
  }
  const code = (currencyCode || 'USD').toUpperCase();
  const isoFd = getIso4217CurrencyFractionDigits(code);
  const requestedFd = options.moneyFractionDigits;
  const displayFd = isoFd === 0 ? 0 : Math.max(isoFd, requestedFd ?? isoFd);
  const amountMajor = minorUnitsToMajorUnits(amountMinor, code);
  const body = !Number.isFinite(amountMajor)
    ? getFormattedAmount(0, code, displayFd)
    : getFormattedAmount(amountMajor, code, displayFd);
  if (options.leadingPlusForPositive && amountMajor > 0) {
    return '+' + body;
  }
  return body;
}

/** Per-subscription ARR delta in minor units (new_arr − original_arr). */
export function computeArrChangeForSubscription(
  subscription: OptimizedSubscription
): number {
  return (subscription.new_arr ?? 0) - (subscription.original_arr ?? 0);
}

export type ArrImpactAggregate = {
  arrChange: number;
  arrPercentageChange: number;
  hasMultipleCurrencies: boolean;
  currency: string | null;
};

/**
 * Signed label for a total ARR change (minor units), e.g. publish / preview headers.
 */
export function formatSignedArrImpactLabel(
  aggregate: Pick<
    ArrImpactAggregate,
    'arrChange' | 'currency' | 'hasMultipleCurrencies'
  >,
  moneyFractionDigits?: number
): string {
  return formatArrForDisplay(aggregate.arrChange, aggregate.currency ?? 'USD', {
    hasMultipleCurrencies: aggregate.hasMultipleCurrencies,
    leadingPlusForPositive: true,
    moneyFractionDigits,
  });
}

/**
 * Aggregate ARR metrics; {@code arrChange} is the sum of per-subscription minor deltas (same
 * currency only). {@code arrPercentageChange} uses minor totals (ratio equals major ratio).
 */
export function computeArrImpactForSubscriptionUpdates(
  subscriptionUpdates: OptimizedSubscription[]
): ArrImpactAggregate {
  if (subscriptionsHaveMultipleCurrencies(subscriptionUpdates)) {
    return {
      arrChange: 0,
      arrPercentageChange: 0,
      hasMultipleCurrencies: true,
      currency: null,
    };
  }
  if (subscriptionUpdates.length === 0) {
    return {
      arrChange: 0,
      arrPercentageChange: 0,
      hasMultipleCurrencies: false,
      currency: null,
    };
  }

  const currency = (subscriptionUpdates[0]!.currency ?? 'USD').toUpperCase();
  const arrChange = subscriptionUpdates.reduce(
    (sum, sub) => sum + computeArrChangeForSubscription(sub),
    0
  );
  const totalOriginalMinor = subscriptionUpdates.reduce(
    (total, sub) => total + (sub.original_arr ?? 0),
    0
  );
  const arrPercentageChange =
    totalOriginalMinor > 0 ? (arrChange / totalOriginalMinor) * 100 : 0;

  return {
    arrChange,
    arrPercentageChange,
    hasMultipleCurrencies: false,
    currency,
  };
}
